import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { OmaLummeApp } from '../../omaLummeContext';

import Button from '../Common/Button';
import UserSwap from './UserSwap';

import styles from './styles/Header.module.scss';

import { logUserOut } from '../../utils/login';

// Actions.
import { getCampaignPriceOffers } from '../../actions';
// Images.
import logo from '../../logo.svg';
import { menuIcon } from '../../icons';
import Select from '../Common/Select';
import { generateMeteringPointOptions } from '../../utils/consumption';
import Loading from '../Common/Loading';

export function useWindowSize(): number[] {
  const [size, setSize] = useState([window.innerWidth]);
  useEffect(() => {
    const handleResize = (): void => {
      setSize([window.innerWidth]);
    };
    window.addEventListener('resize', handleResize);
    return (): void => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  return size;
}

type HeaderProps = {
  nav?: {
    navOpen: boolean;
    toggleNavOpen: (value: React.SetStateAction<boolean>) => void;
  };
  userSwap?: {
    userSwapOpen: boolean;
    toggleUserSwapOpen: (value: React.SetStateAction<boolean>) => void;
  };
};

const Header: React.FunctionComponent<HeaderProps> = (props: HeaderProps) => {
  const dispatch = OmaLummeApp.useDispatch();

  const { basicData, loadingBasicData, users, contracts, offers, selectedMeteringPoint } = OmaLummeApp.useState();

  const { t, i18n } = useTranslation();
  const navOpen = props.nav?.navOpen;
  const toggleNavOpen = props.nav?.toggleNavOpen;
  const toggleUserSwapOpen = props.userSwap?.toggleUserSwapOpen;
  const [offerUser, setOfferUser] = React.useState<number>(0);

  const optionString = selectedMeteringPoint
    ? `${selectedMeteringPoint.meteringPointId !== '' ? selectedMeteringPoint.meteringPointId : 'null'}-${selectedMeteringPoint.contractNo}-${
        selectedMeteringPoint.contractCode
      }`
    : ``;

  const [activeOption, setActiveOption] = React.useState<string>(optionString);

  React.useEffect(() => {
    async function checkCampaignPriceOffers(): Promise<void> {
      const fetchedOffers = await getCampaignPriceOffers(users.activeUser.userId, i18n.language);

      dispatch(['changeOffers', fetchedOffers ?? []]);
    }

    if (users.activeUser.endpoint !== 'anon' && (!offers || offerUser !== users.activeUser.userId)) {
      setOfferUser(users.activeUser.userId);
      checkCampaignPriceOffers();
    }
  }, [users.activeUser.userId, users.activeUser.endpoint, offers]);

  React.useEffect(() => {
    const optionString = selectedMeteringPoint
      ? `${
          selectedMeteringPoint.meteringPointId && selectedMeteringPoint.meteringPointId.length > 0 ? selectedMeteringPoint.meteringPointId : 'null'
        }-${selectedMeteringPoint.contractNo}-${selectedMeteringPoint.contractCode}`
      : ``;

    setActiveOption(optionString);
  }, [basicData, selectedMeteringPoint]);

  React.useEffect(() => {
    const meteringPoint = basicData
      ? basicData.find((mp) => mp.GSRN === activeOption.split('-')[0] && mp.contractNo === activeOption.split('-')[1])
      : undefined;

    const otherContract =
      !meteringPoint && basicData && basicData.length > 0
        ? basicData.find((d) => d.GSRN === null && activeOption.split('-')[0] === 'null' && d.contractNo === activeOption.split('-')[1])
        : undefined;

    if (otherContract) {
      dispatch([
        'changeSelectedMeteringPoint',
        {
          customerId: parseInt(otherContract.customerNo),
          contractCode: otherContract.contractCode,
          contractNo: parseInt(otherContract.contractNo),
          meteringPointId: otherContract.GSRN,
        },
      ]);
    } else if (
      meteringPoint &&
      meteringPoint.GSRN &&
      !(selectedMeteringPoint?.contractNo === parseInt(meteringPoint.contractNo) && selectedMeteringPoint?.meteringPointId === meteringPoint.GSRN)
    ) {
      dispatch([
        'changeSelectedMeteringPoint',
        {
          customerId: parseInt(meteringPoint.customerNo),
          contractCode: meteringPoint.contractCode,
          contractNo: parseInt(meteringPoint.contractNo),
          meteringPointId: meteringPoint.GSRN,
        },
      ]);
    }
  }, [activeOption, contracts, basicData]);

  const meteringPointOptions = generateMeteringPointOptions(basicData ?? [], false, [], true);

  return (
    <>
      <header className={styles.header}>
        <Link
          to="/"
          className={styles.logo}
          onClick={(): void => {
            if (toggleNavOpen && toggleUserSwapOpen) {
              toggleNavOpen(false);
              toggleUserSwapOpen(false);
            }
          }}
        >
          <img src={logo} alt="Lumme Energia" />
        </Link>

        <div className={styles.dropdowns}>
          <form>
            {loadingBasicData && <Loading size="small" />}
            {!loadingBasicData && (
              <Select
                options={meteringPointOptions}
                contractGrouping={meteringPointOptions.length > 1}
                activeOption={activeOption}
                setActiveOption={setActiveOption}
                idToggle
                search={meteringPointOptions.length > 5 ? t(`consumption:consumption.searchMeteringPoint`) : undefined}
              />
            )}
          </form>

          {props.nav && props.userSwap && <UserSwap nav={props.nav} userSwap={props.userSwap} />}
        </div>

        {toggleNavOpen && toggleUserSwapOpen && (
          <button
            className={styles.menuBurger}
            onClick={(): void => {
              toggleNavOpen(!navOpen);
              toggleUserSwapOpen(false);
            }}
          >
            <img src={menuIcon} alt={`${t('header:functional.openMenu')}`} />
          </button>
        )}
        <div className={`${styles.logout} ${styles.noBorder}`}>
          <Button className="buttonWhite" onClick={(): void => logUserOut()}>
            {t('header:functional.logout')}
          </Button>
        </div>
      </header>

      <form>
        {loadingBasicData && <Loading size="small" />}
        {!loadingBasicData && (
          <Select
            options={meteringPointOptions}
            activeOption={activeOption}
            setActiveOption={setActiveOption}
            idToggle
            search={meteringPointOptions.length > 5 ? t(`consumption:consumption.searchMeteringPoint`) : undefined}
          />
        )}
      </form>
    </>
  );
};

export default Header;
